/**
 * Google Places API loader (singleton, idempotent).
 * - Fetches the API key from /api/integrations/maps-key once per session.
 * - Loads https://maps.googleapis.com/maps/api/js once.
 * - Returns a promise that resolves when window.google.maps.places is ready.
 * - Rejects (and components fall back to plain text) if no key is configured.
 */
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

let loadingPromise = null;
let keyPromise = null;

async function fetchMapsKey() {
  if (keyPromise) return keyPromise;
  keyPromise = axios
    .get(`${BACKEND_URL}/api/integrations/maps-key`, { timeout: 8000 })
    .then((r) => (r.data?.key || '').trim())
    .catch(() => '');
  return keyPromise;
}

export async function ensureGooglePlacesLoaded() {
  if (typeof window === 'undefined') throw new Error('SSR not supported');
  if (window.google?.maps?.places) return window.google.maps.places;
  if (loadingPromise) return loadingPromise;

  loadingPromise = (async () => {
    const key = await fetchMapsKey();
    if (!key) throw new Error('Google Places key not configured');
    return new Promise((resolve, reject) => {
      const existing = document.querySelector('script[data-google-places-loader]');
      if (existing) {
        existing.addEventListener('load', () => resolve(window.google?.maps?.places));
        existing.addEventListener('error', () => reject(new Error('Failed to load Google Places')));
        return;
      }
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(key)}&libraries=places&loading=async`;
      script.async = true;
      script.defer = true;
      script.dataset.googlePlacesLoader = '1';
      script.onload = () => {
        if (window.google?.maps?.places) resolve(window.google.maps.places);
        else reject(new Error('Google Places library not present after load'));
      };
      script.onerror = () => reject(new Error('Failed to load Google Places'));
      document.head.appendChild(script);
    });
  })();

  return loadingPromise;
}
